import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { adminApi } from "../lib/api";
import {
  FolderKanban,
  Briefcase,
  Layers,
  GraduationCap,
  BadgeCheck,
  Trophy,
  Inbox,
  FileText,
  ArrowRight,
  Clock,
} from "lucide-react";

const CARDS = [
  { key: "projects", label: "Projects", icon: FolderKanban, to: "/admin/projects" },
  { key: "experiences", label: "Experience", icon: Briefcase, to: "/admin/experience" },
  { key: "skills", label: "Skills", icon: Layers, to: "/admin/skills" },
  { key: "education", label: "Education", icon: GraduationCap, to: "/admin/education" },
  { key: "certifications", label: "Certifications", icon: BadgeCheck, to: "/admin/certifications" },
  { key: "achievements", label: "Achievements", icon: Trophy, to: "/admin/achievements" },
  { key: "messages", label: "Messages", icon: Inbox, to: "/admin/messages" },
];

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    adminApi.dashboard().then(setStats).catch(() => setStats({ counts: {}, last_updated: null }));
  }, []);

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div>
        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-cyan-300 mb-2">Overview</div>
        <h1 className="font-display text-3xl text-white font-medium">Dashboard</h1>
        <p className="text-slate-400 text-sm mt-1">Manage every section of your live portfolio.</p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {CARDS.map((c, i) => (
          <motion.div
            key={c.key}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: i * 0.04 }}
          >
            <Link
              to={c.to}
              data-testid={`dash-card-${c.key}`}
              className="block glass glass-hover rounded-2xl p-5"
            >
              <div className="flex items-center justify-between">
                <div className="w-9 h-9 rounded-lg border border-white/10 bg-white/[0.03] grid place-items-center">
                  <c.icon size={15} className="text-cyan-300" />
                </div>
                <ArrowRight size={14} className="text-slate-500" />
              </div>
              <div className="mt-4 font-display text-3xl text-white font-medium">
                {stats?.counts?.[c.key] ?? "—"}
              </div>
              <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-slate-500 mt-1">
                {c.label}
              </div>
            </Link>
          </motion.div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 glass rounded-2xl p-5">
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-cyan-300 mb-3 flex items-center gap-2">
            <Clock size={12} /> Last activity
          </div>
          <div className="text-white font-display text-lg">{fmtDate(stats?.last_updated)}</div>
          <p className="mt-2 text-sm text-slate-400">
            Most recent content edit across the CMS. Public site updates instantly.
          </p>
        </div>
        <div className="glass rounded-2xl p-5">
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-cyan-300 mb-3 flex items-center gap-2">
            <FileText size={12} /> Resume
          </div>
          <div className="text-white font-display text-lg">
            {stats?.resume_uploaded ? "Uploaded" : "Missing"}
          </div>
          <Link
            to="/admin/resume"
            className="mt-3 inline-flex items-center gap-1 text-sm text-cyan-300 hover:text-cyan-200"
          >
            Manage resume <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
